export const toneClass = (kind, isDark) => {
  switch (kind) {
    case 'token':
      return isDark ? 'text-brand-300' : 'text-brand-700';
    case 'meta':
      // Deliberately not the token colour — 'has:' is a different kind of
      // word from a column name, and reads wrong if the two look alike.
      return isDark ? 'text-emerald-300' : 'text-emerald-600';
    case 'negation':
      return isDark ? 'text-gray-500' : 'text-gray-400';
    case 'quoted':
      return isDark ? 'text-teal-300' : 'text-teal-700';
    case 'error':
      return isDark ? 'text-red-400' : 'text-red-600';
    default:
      return isDark ? 'text-white' : 'text-gray-900';
  }
};

const WHITESPACE = /\s/;
const COLUMN_PREFIX = /^[A-Za-z_][A-Za-z0-9_]*:/;

// The meta-prefix whose value is a column name rather than text to match.
const PRESENCE_PREFIX = 'has';

const scanQuotedValue = (query, start) => {
  const n = query.length;
  let i = start + 1;
  while (i < n && query[i] !== '"') {
    if (query[i] === '\\' && i + 1 < n) i++;
    i++;
  }
  if (i < n) i++; // consume closing quote
  return query.slice(start, i);
};

// Splits a raw search-bar query into display segments for the highlight
// overlay. Mirrors (but does not have to exactly match) the backend
// grammar in the arc-2ef.2 parser: `-`? (column ':')? (bare_word | "quoted"),
// plus the `has:<column>` presence prefix from arc-2ef.14, which gets its own
// 'meta' kind so it never reads as one of the column tokens it takes.
export const tokenizeQueryForHighlight = (query, knownTokens) => {
  const known = new Set(Array.from(knownTokens || [], (t) => String(t).toLowerCase()));
  const segments = [];
  const n = query.length;
  let i = 0;

  const pushText = (text) => {
    if (text) segments.push({ text, kind: 'text' });
  };

  while (i < n) {
    if (WHITESPACE.test(query[i])) {
      const start = i;
      while (i < n && WHITESPACE.test(query[i])) i++;
      pushText(query.slice(start, i));
      continue;
    }

    if (query[i] === '-') {
      segments.push({ text: '-', kind: 'negation' });
      i++;
    }

    const columnMatch = COLUMN_PREFIX.exec(query.slice(i));
    if (columnMatch) {
      const prefix = columnMatch[0];
      const word = prefix.slice(0, -1);

      if (word.toLowerCase() === PRESENCE_PREFIX) {
        segments.push({ text: prefix, kind: 'meta' });
        i += prefix.length;

        // What follows names a column, so it is tagged like one rather than
        // as a value: 'has:emial' should read as wrong where the user typed it.
        const start = i;
        while (i < n && !WHITESPACE.test(query[i])) i++;
        const column = query.slice(start, i);
        if (column) {
          segments.push({ text: column, kind: known.has(column.toLowerCase()) ? 'token' : 'error' });
        }
        continue;
      }

      segments.push({ text: prefix, kind: known.has(word.toLowerCase()) ? 'token' : 'error' });
      i += prefix.length;

      if (query[i] === '"') {
        const value = scanQuotedValue(query, i);
        segments.push({ text: value, kind: 'quoted' });
        i += value.length;
      } else {
        const start = i;
        while (i < n && !WHITESPACE.test(query[i])) i++;
        pushText(query.slice(start, i));
      }
      continue;
    }

    if (query[i] === '"') {
      const value = scanQuotedValue(query, i);
      segments.push({ text: value, kind: 'quoted' });
      i += value.length;
      continue;
    }

    const start = i;
    while (i < n && !WHITESPACE.test(query[i])) i++;
    pushText(query.slice(start, i));
  }

  return segments;
};
